import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BannersConfig, EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { CardThemeSurface, SportsThemedCardHeaderDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { CasinoGameViewModel, SportsGamesBannerViewModel } from '@frontend/sports/types/components/banners/models';
import { BannerGamesDisplayType, UpSellCrossSellLDService } from '@frontend/sports/upsell-xsell/common/feature';
import { UserService } from '@frontend/sports/user/feature';
import { DsButton } from '@frontend/ui/button';
import { DsCardHeader } from '@frontend/ui/card-header';
import { DeviceService, WINDOW } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { KycStatus, KycStatusService } from '@frontend/vanilla/shared/kyc';
import { Observable } from 'rxjs';

import { ArcRestrictionService } from '../arc/arc-restriction.service';
import { GeolocationCheckService } from '../geo-location/geo-location-check.service';
import { BannerGamesMultiRowComponent } from './banner-games-multi-row.component';
import { BannerGamesSingleRowComponent } from './banner-games-single-row.component';
import { BannerTrackingService } from './banner-tracking.service';
import { BannersService } from './banners.service';
import { GamesService } from './games.service';

@Component({
    selector: 'ms-banner-games',
    templateUrl: './banner-games.component.html',
    imports: [
        BannerGamesMultiRowComponent,
        BannerGamesSingleRowComponent,
        AsyncPipe,
        IconCustomComponent,
        DsCardHeader,
        SportsThemedCardHeaderDirective,
        DsButton,
    ],
    host: {
        'class': 'banner-games',
        '[class.hidden]': 'hidden()',
    },
})
export class BannerGamesComponent implements OnInit {
    model = input.required<SportsGamesBannerViewModel>();
    position = input<string>('');
    readonly bannerDataLoaded = output<boolean>();

    readonly hidden = signal<boolean>(true);
    isEpcotEnabled = signal(false);
    games: CasinoGameViewModel[] = [];
    link: string;
    hideGamingCrossSellByArc$: Observable<boolean>;
    bannerGamesDisplayType = BannerGamesDisplayType;

    private readonly destroyRef = inject(DestroyRef);
    private readonly user = inject(UserService);
    private readonly urlHelper = inject(UrlHelperService);
    private readonly bannerConfig = inject(BannersConfig);
    private readonly bannersService = inject(BannersService);
    private readonly trackingService = inject(BannerTrackingService);
    private readonly deviceService = inject(DeviceService);
    private readonly geolocationService = inject(GeolocationCheckService);
    private readonly redirectService = inject(RedirectHelperService);
    private readonly gamesService = inject(GamesService);
    private readonly arcRestrictionService = inject(ArcRestrictionService);
    private readonly kycStatusService = inject(KycStatusService);
    readonly ldBannerGamesDisplayType = inject(UpSellCrossSellLDService).bannerGamesDisplayType();
    private readonly epcotConfig = inject(EpcotConfig);

    ngOnInit(): void {
        this.isEpcotEnabled.update(() => this.epcotConfig.isEnabled);
        this.hideGamingCrossSellByArc$ = this.arcRestrictionService.gamingCrossSellEnabled$;
        this.user.onUserLogin$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getCasinoGamesByKycStatus());
        this.user.onUserLogout$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getCasinoGamesByKycStatus());
        this.getCasinoGamesByKycStatus();
    }

    themedSurface: CardThemeSurface = {
        'betboo-br': 'low',
        'sportingbet-br': 'low',
    };

    private getCasinoGamesByKycStatus(): void {
        if (this.user.isAuthenticated && this.bannerConfig.isKYCCheckEnabled) {
            this.kycStatusService.kycStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status: KycStatus | null) => {
                this.getCasinoGames(status?.kycVerified);
            });
        } else {
            this.getCasinoGames();
        }
    }

    openGame(openedGame: { game: CasinoGameViewModel; index: number }): void {
        const url = this.gamesService.getLaunchUrl(openedGame.game.name);
        const trackingData = {
            name: openedGame.game.name,
            index: openedGame.index,
            displayName: openedGame.game.title,
            provider: openedGame.game.provider,
            lobbyPosition: this.position(),
            pageName: '',
            url,
        };

        if (!this.user.isAuthenticated) {
            this.trackingService.trackGymlClick(trackingData);
            this.redirectService.goToLogin({ appendReferrer: true });

            return;
        }

        this.trackingService.trackGymlLaunch(trackingData);

        this.geolocationService.checkGeolocation(() => {
            if (this.deviceService.isMobile) {
                this._window.open(url, '_self');
            } else {
                this._window.open(url, '_blank', 'width=800,height=600');
            }
        });
    }

    openAll(): void {
        this.trackingService.trackGymlSeeAllCrossSell(this.position(), this.link);
        this.redirectService.goToPage(this.link);
    }

    private getCasinoGames(isKycVerified?: boolean): void {
        this.bannersService.getLobbyGames(isKycVerified).then((games) => {
            if (games.length < this.bannerConfig.games.minGames) {
                this.bannerDataLoaded.emit(false);

                return;
            }

            this.hidden.set(false);
            this.link = this.getBannerLink();
            this.games = games.slice(0, this.bannerConfig.games.maxGames);
            this.bannerDataLoaded.emit(true);
        });
    }

    private getBannerLink(): string {
        const link = this.model().link;
        if (link && link?.href.startsWith('/')) {
            return this.urlHelper.getUrl(link.href);
        }
        if (link) {
            link.target = link.target || '_self';

            return link.href;
        }

        return '#';
    }

    private readonly _window = inject(WINDOW);
}
